import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { apiFetch } from '@/lib/auth';

/**
 * Welle 9 — Link-Verwaltung. Listet die vom Berater erzeugten Magic-Links mit Status
 * (aktiv/geplant/abgelaufen/widerrufen) und erlaubt das Widerrufen einzelner Links.
 */
interface LinkRow {
  token: string;
  url: string;
  kind: 'call' | 'agent' | 'group';
  targetLabel: string | null;
  expectedName: string | null;
  agentName: string | null;
  groupName: string | null;
  validFrom: string | null;
  validUntil: string | null;
  usedCount: number;
  createdAt: string;
  status: 'active' | 'upcoming' | 'expired' | 'revoked';
}

const KIND_LABEL: Record<LinkRow['kind'], string> = {
  agent: 'Persönlich',
  group: 'Gruppe',
  call: 'Gespräch',
};

const STATUS_META: Record<LinkRow['status'], { label: string; color: string; bg: string }> = {
  active: { label: 'Aktiv', color: '#22C55E', bg: 'rgba(34,197,94,0.14)' },
  upcoming: { label: 'Geplant', color: '#3B82F6', bg: 'rgba(59,130,246,0.14)' },
  expired: { label: 'Abgelaufen', color: '#8E8E8E', bg: 'rgba(142,142,142,0.14)' },
  revoked: { label: 'Widerrufen', color: '#EF4444', bg: 'rgba(239,68,68,0.14)' },
};

function fmtAppointment(from: string | null, until: string | null): string | null {
  if (!from) return null;
  const f = new Date(from);
  if (isNaN(f.getTime())) return null;
  const day = f.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const t = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const u = until ? new Date(until) : null;
  if (u && !isNaN(u.getTime())) return `${day} · ${t(f)}–${t(u)} Uhr`;
  return `${day} · ab ${t(f)} Uhr`;
}

function fmtCreated(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function LinkManager({ refreshSignal = 0 }: { refreshSignal?: number }) {
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyToken, setBusyToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch('/api/links/mine');
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      setErr('Links konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, refreshSignal]);

  const revoke = async (token: string) => {
    setBusyToken(token);
    try {
      const r = await apiFetch(`/api/links/${encodeURIComponent(token)}/revoke`, { method: 'POST' });
      if (r.ok) setLinks((ls) => ls.map((l) => (l.token === token ? { ...l, status: 'revoked' } : l)));
    } catch { /* still shown as active; nutzer kann erneut versuchen */ }
    finally { setBusyToken(null); }
  };

  const copy = async (l: LinkRow) => {
    try {
      await navigator.clipboard.writeText(l.url);
      setCopiedToken(l.token);
      setTimeout(() => setCopiedToken(null), 1500);
    } catch { /* Clipboard nicht verfügbar */ }
  };

  return (
    <div className="now-card" style={{ display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div className="nc-eyebrow">Verwaltung</div>
          <div className="nc-title" style={{ marginTop: 4 }}>Meine Links</div>
        </div>
        <button onClick={() => load()} style={refreshBtn} title="Aktualisieren">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>

      {loading && <div style={{ color: 'var(--muted, #8aa)', fontSize: 13, padding: '8px 0' }}>Wird geladen…</div>}
      {err && <div style={{ color: '#d8785a', fontSize: 13, padding: '8px 0' }}>{err}</div>}
      {!loading && !err && links.length === 0 && (
        <div style={{ color: 'var(--muted, #8aa)', fontSize: 13, padding: '12px 0', lineHeight: 1.5 }}>
          Noch keine Links erzeugt. Erstelle links einen persönlichen oder Gruppen-Link.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {links.map((l) => {
          const sm = STATUS_META[l.status];
          const target = l.kind === 'group'
            ? (l.groupName ? `Gruppe ${l.groupName}` : l.targetLabel || 'Gruppe')
            : (l.agentName || l.targetLabel || 'Persönlich');
          const appt = fmtAppointment(l.validFrom, l.validUntil);
          return (
            <div key={l.token} style={rowStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={kindBadge}>{KIND_LABEL[l.kind]}</span>
                <span style={{ ...statusBadge, color: sm.color, background: sm.bg }}>{sm.label}</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{target}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', fontSize: 12, color: 'var(--muted, #8aa)', marginTop: 6 }}>
                {l.expectedName && <span>Experte: {l.expectedName}</span>}
                {appt && <span>Termin: {appt}</span>}
                <span>Genutzt: {l.usedCount}×</span>
                <span>Erstellt: {fmtCreated(l.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => copy(l)} style={actionBtn} disabled={l.status === 'revoked'}>
                  {copiedToken === l.token ? '✓ Kopiert' : 'Link kopieren'}
                </button>
                {l.status !== 'revoked' && (
                  <button onClick={() => revoke(l.token)} style={revokeBtn} disabled={busyToken === l.token}>
                    {busyToken === l.token ? 'Wird widerrufen…' : 'Widerrufen'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const refreshBtn: CSSProperties = {
  width: 30, height: 30, borderRadius: 8, border: '1px solid var(--hairline, #232323)',
  background: 'transparent', color: 'var(--muted, #8aa)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const rowStyle: CSSProperties = {
  border: '1px solid var(--hairline, #232323)', borderRadius: 10, padding: '12px 14px',
  background: 'var(--bg-elev-2)',
};
const kindBadge: CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
  color: 'var(--fg-soft, #E8E8E8)', background: 'var(--bg-elev-3)',
  padding: '2px 7px', borderRadius: 6,
};
const statusBadge: CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
  padding: '2px 7px', borderRadius: 6,
};
const actionBtn: CSSProperties = {
  border: '1px solid var(--border, #2A2A2A)', background: 'transparent', color: 'inherit',
  borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const revokeBtn: CSSProperties = {
  border: 'none', background: 'rgba(239,68,68,0.85)', color: '#fff',
  borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
